import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { filter, switchMap } from 'rxjs/operators';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { DataType } from 'src/app/components/data-type-widgets/data-type/data-type-component';
import { FieldConfigDTO } from '../../../../system/system-dtos';
import { AutoWithdrawalsConfigDTO, TierPriorityDTO, avsResultValues } from './auto-withdrawals-config.model';
import { AutoWithdrawalsConfigService } from './auto-withdrawals-config.service';
import { AutoWithdrawalsTierComponent } from './auto-withdrawals-tier/auto-withdrawals-tier.component';
import { SystemService } from '../../../../system/system.service';
import { ConfirmationModalComponent } from '../../../dialogs/confirmation-modal/confirmation-modal.component';
import { BinRangeExclusionComponent } from './bin-range-exclusion/bin-range-exclusion.component';

@Component({
  selector: 'app-auto-withdrawals-config',
  templateUrl: './auto-withdrawals-config.component.html',
  styleUrls: ['./auto-withdrawals-config.component.scss'],
  standalone: false,
})
export class AutoWithdrawalsConfigComponent implements OnInit {
  editMode: boolean = false;

  config: AutoWithdrawalsConfigDTO;

  avsResultValues = avsResultValues;

  constructor(
    private fb: UntypedFormBuilder,
    public dialog: MatDialog,
    private systemService: SystemService,
    private service: AutoWithdrawalsConfigService
  ) {}

  formGroup: UntypedFormGroup;

  moneyConfig: FieldConfigDTO = {
    label: null,
    type: DataType.money,
    name: 'money',
    moneyFormat: 'fullValue',
  };

  articlesLookupConfig: FieldConfigDTO = {
    label: null,
    type: DataType.lookup,
    name: 'withdrawsArticle',
    selectUrl: '/zendesk/lookup',
    lookup: 'zendesk_article',
  };

  ngOnInit(): void {
    this.systemService
      .getMenuConfig('auto-withdrawals-config')
      .pipe(switchMap(() => this.service.getAutoWithdrawalsConfig()))
      .subscribe((e) => this.reloadView(e));
  }

  private buildForm(): void {
    this.formGroup = this.fb.group({
      requestToPendingEnabled: [this.config?.requestToPendingEnabled || false],
      requestedToPendingInMinutes: [this.config?.requestedToPendingInMinutes || 0],
      pendingToAcceptedEnabled: [this.config?.pendingToAcceptedEnabled || false],
      pendingToAcceptedInMinutes: [this.config?.pendingToAcceptedInMinutes || 0],
      maxNumberOfCancelWithdrawalsPerMonth: [this.config?.maxNumberOfCancelWithdrawalsPerMonth || 0],
      withdrawsArticle: [this.config?.withdrawsArticle || ''],
      enableWithdrawCancellation: [this.config?.enableWithdrawCancellation || false],
    });
  }

  editTier(tierId: number): void {
    this.dialog
      .open(AutoWithdrawalsTierComponent, { data: { tierId } })
      .afterClosed()
      .pipe(
        filter((d) => !!d),
        switchMap(() => this.service.getAutoWithdrawalsConfig())
      )
      .subscribe((e) => this.reloadView(e));
  }

  removeTier(tierId: number): void {
    this.dialog
      .open(ConfirmationModalComponent, {
        data: { note: `Are you sure you want to delete tier #${tierId}?` },
        height: '250px',
        width: '300px',
      })
      .afterClosed()
      .pipe(
        filter((d) => !!d),
        switchMap(() => this.service.removeTier(tierId)),
        switchMap(() => this.service.getAutoWithdrawalsConfig())
      )
      .subscribe((e) => this.reloadView(e));
  }

  update(): void {
    const formValue = this.formGroup.value;
    this.service
      .updateAutoWithdrawalsConfig(formValue)
      .pipe(switchMap(() => this.service.getAutoWithdrawalsConfig()))
      .subscribe((e) => this.reloadView(e));
  }

  reloadView(config: AutoWithdrawalsConfigDTO): void {
    this.config = config;
    this.config.tiers = this.config.tiers.sort((a, b) => a.priority - b.priority);
    this.buildForm();
    this.editMode = false;
  }

  drop(event: any): void {
    moveItemInArray(this.config.tiers, event.previousIndex, event.currentIndex);

    // const tiersNumber = this.config.tiers.length;
    this.service
      .reorder({
        priorities: this.config.tiers.map<TierPriorityDTO>((tier, index) => ({
          tierId: tier.id,
          priority: index,
        })),
      })
      .pipe(
        filter((d) => !!d),
        switchMap(() => this.service.getAutoWithdrawalsConfig())
      )
      .subscribe((e) => this.reloadView(e));
  }

  addNewTier(): void {
    this.dialog
      .open(AutoWithdrawalsTierComponent, { data: { priority: this.config.tiers.length } })
      .afterClosed()
      .pipe(
        filter((d) => !!d),
        switchMap(() => this.service.getAutoWithdrawalsConfig())
      )
      .subscribe((e) => this.reloadView(e));
  }

  addNewBinRangeExclusion(): void {
    this.dialog
      .open(BinRangeExclusionComponent)
      .afterClosed()
      .pipe(
        filter((d) => !!d),
        switchMap(() => this.service.getAutoWithdrawalsConfig())
      )
      .subscribe((e) => this.reloadView(e));
  }

  editBinRangeExclusion(binRangeExclusionId: number): void {
    this.dialog
      .open(BinRangeExclusionComponent, { data: { binRangeExclusionId } })
      .afterClosed()
      .pipe(
        filter((d) => !!d),
        switchMap(() => this.service.getAutoWithdrawalsConfig())
      )
      .subscribe((e) => this.reloadView(e));
  }

  removeBinRangeExclusion(binRangeExclusionId: number): void {
    this.dialog
      .open(ConfirmationModalComponent, {
        data: { note: `Are you sure you want to delete Bin Range Exclusion #${binRangeExclusionId}?` },
      })
      .afterClosed()
      .pipe(
        filter((d) => !!d),
        switchMap(() => this.service.removeBinRangeExclusion(binRangeExclusionId)),
        switchMap(() => this.service.getAutoWithdrawalsConfig())
      )
      .subscribe((e) => this.reloadView(e));
  }
}
